import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useRef } from "react";
import { Download, Copy } from "lucide-react";
import { useNavigate } from "react-router";
import { SetupLayout } from "./SetupLayout";
import { Button } from "../../primitives/Button";
import { useAuth } from "../../provider";
import { useAuthConfig } from "../../config";
export function SetupMFAVerifyAndBackup() {
    const navigate = useNavigate();
    const { authClient } = useAuth();
    const { brandName } = useAuthConfig();
    const [code, setCode] = useState(["", "", "", "", "", ""]);
    const [verified, setVerified] = useState(false);
    const [saved, setSaved] = useState(false);
    const [copied, setCopied] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [backupCodes, setBackupCodes] = useState([]);
    const inputRefs = useRef([]);
    const handleChange = (index, value) => {
        if (value && !/^\d$/.test(value))
            return;
        const newCode = [...code];
        newCode[index] = value;
        setCode(newCode);
        if (value && index < 5) {
            inputRefs.current[index + 1]?.focus();
        }
        if (value && index === 5 && newCode.every((d) => d !== "")) {
            handleVerifyWithCode(newCode.join(""));
        }
    };
    const handleVerifyWithCode = async (fullCode) => {
        setIsLoading(true);
        setError(null);
        try {
            const res = await authClient.verifyMFASetup(fullCode);
            setBackupCodes(res.backup_codes ?? []);
            setVerified(true);
        }
        catch {
            setCode(["", "", "", "", "", ""]);
            inputRefs.current[0]?.focus();
            setError("Incorrect code. Please try again.");
        }
        finally {
            setIsLoading(false);
        }
    };
    const handleKeyDown = (index, e) => {
        if (e.key === "Backspace" && !code[index] && index > 0) {
            inputRefs.current[index - 1]?.focus();
        }
    };
    const slug = brandName.toLowerCase().replace(/\s+/g, "-");
    const handleDownload = () => {
        const content = `${brandName} - Backup Codes\n\n${backupCodes.join("\n")}\n\nKeep these codes in a safe place. Each code can only be used once.`;
        const blob = new Blob([content], { type: "text/plain" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${slug}-backup-codes.txt`;
        a.click();
        URL.revokeObjectURL(url);
    };
    const handleCopy = () => {
        navigator.clipboard.writeText(backupCodes.join("\n"));
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };
    const handleDone = () => {
        navigate("/setup/complete");
    };
    return (_jsx(SetupLayout, { currentStep: 1, children: _jsxs("div", { children: [_jsxs("div", { className: "mb-8", children: [_jsx("h1", { className: "text-duvi-text-primary mb-2", style: {
                                fontSize: "var(--text-xl)",
                                fontWeight: "var(--font-weight-semibold)",
                                lineHeight: "var(--line-height-heading)",
                            }, children: "Verify your authenticator" }), _jsx("p", { className: "text-duvi-text-secondary mb-8", style: {
                                fontSize: "var(--text-sm)",
                                lineHeight: "var(--line-height-body)",
                            }, children: "Enter the 6-digit code from your authenticator app." }), _jsx("div", { className: "flex gap-2 mb-3", children: code.map((digit, index) => (_jsx("input", { ref: (el) => (inputRefs.current[index] = el), type: "text", inputMode: "numeric", maxLength: 1, value: digit, onChange: (e) => handleChange(index, e.target.value), onKeyDown: (e) => handleKeyDown(index, e), disabled: verified, className: "w-full aspect-square bg-duvi-bg border border-duvi-border rounded-[6px] text-center text-duvi-text-primary focus:outline-none focus:border-duvi-accent transition-colors disabled:opacity-50", style: {
                                    fontSize: "var(--text-2xl)",
                                    fontWeight: "var(--font-weight-bold)",
                                    fontFamily: "monospace",
                                } }, index))) }), error && !verified && (_jsx("div", { className: "mb-4 text-duvi-destructive text-center", style: { fontSize: "var(--text-sm)" }, children: error })), isLoading && !verified && (_jsx("div", { className: "mb-4 text-duvi-text-secondary text-center", style: { fontSize: "var(--text-sm)" }, children: "Verifying..." }))] }), verified && _jsx("div", { className: "border-t border-duvi-border mb-8" }), verified && (_jsxs("div", { className: "rounded-[8px] p-6 border", style: {
                        backgroundColor: "rgba(251, 191, 36, 0.08)",
                        borderColor: "rgba(251, 191, 36, 0.2)",
                    }, children: [_jsx("h2", { className: "mb-2", style: {
                                fontSize: "var(--text-lg)",
                                fontWeight: "var(--font-weight-semibold)",
                                lineHeight: "var(--line-height-heading)",
                                color: "#FBBF24",
                            }, children: "Save your backup codes" }), _jsx("p", { className: "text-duvi-text-secondary mb-4", style: { fontSize: "var(--text-xs)" }, children: "Each code can only be used once." }), _jsx("div", { className: "grid grid-cols-2 gap-3 mb-4", children: backupCodes.map((backupCode, index) => (_jsx("div", { className: "bg-duvi-bg border border-duvi-border rounded-[6px] px-4 py-2 text-duvi-text-primary text-center", style: {
                                    fontSize: "var(--text-sm)",
                                    fontFamily: "monospace",
                                }, children: backupCode }, index))) }), _jsxs("div", { className: "flex gap-3 mb-4", children: [_jsxs("button", { onClick: handleDownload, className: "flex-1 flex items-center justify-center gap-2 bg-duvi-elevated border border-duvi-border hover:bg-duvi-bg rounded-[6px] px-4 py-2 text-duvi-text-primary transition-colors", style: { fontSize: "var(--text-sm)" }, title: "Download codes", children: [_jsx(Download, { className: "w-4 h-4" }), "Download"] }), _jsxs("button", { onClick: handleCopy, className: "flex-1 flex items-center justify-center gap-2 bg-duvi-elevated border border-duvi-border hover:bg-duvi-bg rounded-[6px] px-4 py-2 text-duvi-text-primary transition-colors", style: { fontSize: "var(--text-sm)" }, title: "Copy codes", children: [_jsx(Copy, { className: "w-4 h-4" }), copied ? "Copied!" : "Copy"] })] }), _jsxs("label", { className: "flex items-start gap-3 cursor-pointer", children: [_jsx("input", { type: "checkbox", checked: saved, onChange: (e) => setSaved(e.target.checked), className: "mt-1 w-4 h-4 rounded border-duvi-border bg-duvi-bg checked:bg-duvi-accent checked:border-duvi-accent focus:outline-none focus:ring-2 focus:ring-duvi-accent/20 cursor-pointer" }), _jsx("span", { className: "text-duvi-text-primary flex-1", style: { fontSize: "var(--text-sm)" }, children: "I've saved my backup codes in a safe place" })] }), error && verified && (_jsx("div", { className: "mt-4 text-duvi-destructive", style: { fontSize: "var(--text-sm)" }, children: error })), _jsx("div", { className: "mt-6", children: _jsx(Button, { variant: "primary", className: "w-full", onClick: handleDone, disabled: !saved || isLoading, children: isLoading ? "Confirming..." : "Done" }) })] }))] }) }));
}
//# sourceMappingURL=SetupMFAVerifyAndBackup.js.map